// iOS WKWebView glitch: after returning from another app (app-switch resume),
// env(safe-area-inset-*) sometimes reads 0 until the next rotation or scroll —
// so the header slides under the status-bar clock and bottom bars slide under
// the home indicator (founder report, build 1.1(13) app-switch test; same bug
// family as the cold-start flakiness fixed by making viewport-fit static).
//
// Fix: mirror env() into --safe-top / --safe-bottom CSS variables via a hidden
// probe element, re-measure on resume/rotation, and REJECT portrait readings
// where top is 0 — the iPhone status bar is never 0 tall in portrait, so such
// a reading IS the glitch and we keep the last good values. Layout CSS reads
// max(env(safe-area-inset-*), var(--safe-*)), so a stale env() no longer
// collapses the padding. Web/desktop is untouched: this installer no-ops off
// native, the vars are never set there, and max() falls back to plain env().

import { Capacitor } from "@capacitor/core";

/** Fired after --safe-top / --safe-bottom change: the tab bars re-measure (lib/tabBarVars). */
export const SAFE_AREA_EVENT = "rq:safearea";

let installed = false;

export function installSafeAreaVarSync(): void {
  if (!Capacitor.isNativePlatform() || installed) return;
  installed = true;

  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:0;height:0;" +
    "pointer-events:none;visibility:hidden;" +
    "padding-top:env(safe-area-inset-top,0px);" +
    "padding-bottom:env(safe-area-inset-bottom,0px);";
  document.body.appendChild(probe);

  // Returns true once a trustworthy reading was applied.
  const apply = (): boolean => {
    const cs = getComputedStyle(probe);
    const top = parseFloat(cs.paddingTop) || 0;
    const bottom = parseFloat(cs.paddingBottom) || 0;
    // Portrait with top=0 is the stale-env() glitch, not a real measurement
    // (landscape legitimately has top=0 — the notch moves to the side).
    if (top === 0 && window.matchMedia("(orientation: portrait)").matches) {
      return false;
    }
    const root = document.documentElement.style;
    root.setProperty("--safe-top", `${top}px`);
    root.setProperty("--safe-bottom", `${bottom}px`);
    window.dispatchEvent(new Event(SAFE_AREA_EVENT));
    return true;
  };

  // env() can settle late after boot / resume — and after a BACKGROUND
  // RELOAD (iOS kills the page under memory pressure and Capacitor reloads
  // it on resume) it can stay 0 well past one second. Sim instrumentation
  // showed ~300ms on a healthy boot; a fixed 1s cutoff was the
  // suspected header-under-clock hole. So: retry until a good reading is
  // actually OBTAINED — 100ms for the first second, then 250ms, capped at
  // 60s as a runaway stop.
  let timer: number | undefined;
  const settle = () => {
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timer = undefined;
    }
    const started = performance.now();
    const tick = () => {
      const elapsed = performance.now() - started;
      if (apply() || elapsed > 60_000) {
        timer = undefined;
        return;
      }
      timer = window.setTimeout(tick, elapsed < 1_000 ? 100 : 250);
    };
    tick();
  };

  settle();
  // Resume can surface through any of these depending on how iOS restored
  // the page (sim showed visibility delivery isn't perfectly deterministic —
  // don't hang recovery off a single event).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") settle();
  });
  window.addEventListener("pageshow", settle);
  window.addEventListener("focus", settle);
  window.addEventListener("resize", settle); // fires on rotation
}
